import React, { useState } from 'react';
import {
  Button,
  Gutter,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
} from '@axiom/ui';
import type { User } from '@axiom/validation';

import { SupportContactsModalConst } from './SupportContactsModal.const';
import { SupportContactsModalDefault } from './SupportContactsModalDefault';
import { SupportContactsModalTechnicalIssue } from './SupportContactsModalTechnicalIssue';
import { SupportContactsModalAxiomAnswers } from './SupportContactsModalAxiomAnswers';
import { SupportContactsModalPeopleAtAxiom } from './SupportContactsModalPeopleAtAxiom';
import { SupportContactsModalOppFeedQuestion } from './SupportContactsModalOppFeedQuestion';
import { SupportContactsModalProfileHelp } from './SupportContactsModalProfileHelp';
import { SupportContactsModalOther } from './SupportContactsModalOther';

export type TalentPartnerUser = Pick<
  User,
  'email' | 'firstName' | 'lastName' | 'fullName'
>;

type SupportContactsModalProps = {
  user: TalentPartnerUser;
  name: string;
  onClose: () => void;
};

export type SupportActionType = keyof typeof SupportContactsModalConst;

const defaultAction: SupportActionType = 'defaultData';

export const SupportContactsModal = ({
  user,
  name,
  onClose,
}: SupportContactsModalProps) => {
  const [actionType, setActionType] =
    useState<SupportActionType>(defaultAction);
  const data = SupportContactsModalConst[actionType];

  const contentMap: Record<string, React.JSX.Element> = {
    somethingElse: <SupportContactsModalOther user={user} name={name} />,
    profileHelp: <SupportContactsModalProfileHelp user={user} name={name} />,
    oppFeedQuestion: <SupportContactsModalOppFeedQuestion />,
    peopleAtAxiom: <SupportContactsModalPeopleAtAxiom />,
    axiomAnswers: <SupportContactsModalAxiomAnswers />,
    technicalIssue: <SupportContactsModalTechnicalIssue />,
  };

  return (
    <Modal size="confirm">
      <ModalHeader name="SUPPORT_HEADER" onClose={onClose}>
        {data.title}
      </ModalHeader>
      <ModalSection>
        {data.desc ? (
          <div data-test="SUPPORT_DESC">
            {data.desc}
            <Gutter bottom="16px" />
          </div>
        ) : null}

        {contentMap[actionType] ?? (
          <SupportContactsModalDefault
            onClick={(nextActionType: SupportActionType) => {
              setActionType(nextActionType);
            }}
          />
        )}
      </ModalSection>
      <ModalFooter>
        {actionType === defaultAction ? null : (
          <Button
            name="BACK_BUTTON"
            pattern="secondary"
            variation="outline"
            onClick={() => {
              setActionType(defaultAction);
            }}
          >
            Back
          </Button>
        )}
        <Button name="CLOSE_BUTTON" pattern="primary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
